/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import { useContext, useState } from 'react';
import DiviFieldCard from '../../../customs/DiviFieldCard/DiviFieldCard';
import { NodeType } from '../../../../backendModels/report.model';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Button, Chip, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '../../../../assets/images/icons/delete.svg?react';
import { ReportsAPIContext } from '../../../../provider/ReportsAPIProvider';
import { defaultRecords } from '../../../../DefaultRecords';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { ApplicationMethod, Medication, MedicationSource } from '../../../../backendModels/medication.model';
import AddMedicationDialog, { AddMedicationDialogResponse } from './AddMedicationDialog';
import { convertToMedicationLabels, MedicationInfoRow } from '../../../../models/medication';
import { CardId } from '../../../../models/diviCard';
import { NodeRecordTypeMap, RecordId } from '../../../../backendModels/records.model';
import { AlarmAdd } from '@mui/icons-material';
import style from './MedicationTable.scss?inline';
import { useCSS } from '../../../../provider/CSSProvider';
import { AddTimerDialog } from './AddTimerDialog';
import dayjs from 'dayjs';
import { sortByHHmm } from '../../../../utils/util';

interface MedicationCardProps {
  nodeType: NodeType;
}

export interface MedicationTimerIdentifier {
  recordId: number;
}

export default function MedicationTable({ nodeType }: MedicationCardProps) {
  useCSS(style);

  const [addMedicationDialogOpen, setAddMedicationDialogOpen] = useState<boolean>(false);

  const [addTimerDialogOpen, setAddTimerDialogOpen] = useState(false);
  const [suggestedTimerName, setSuggestedTimerName] = useState<string>('');
  const [timerCorrelatedRecordId, setTimerCorrelatedRecordId] = useState<number | undefined>();

  const reportAPI = useContext(ReportsAPIContext);
  const nodes = reportAPI.findNodeWithRecord(nodeType, 'medication');
  const records = nodes.flatMap(
    (node) => node.records.filter((record) => record.type === 'medication') as NodeRecordTypeMap['medication'][]
  );

  const recordIdToTimestamp = new Map<RecordId, number>();
  nodes.forEach((node) => {
    node.records.forEach((record) => {
      if (record.id !== undefined && record.type === 'medication') {
        recordIdToTimestamp.set(record.id, node.effectiveTimestamp!);
      }
    });
  });

  const timers = reportAPI.timers!.filter((timer) => timer.identifier as MedicationTimerIdentifier);
  const timerIdentifiers = timers.map((timer) => (timer?.identifier as MedicationTimerIdentifier)?.recordId);

  const medicationsList: readonly Medication[] = reportAPI.sharedData!.medications! || [];
  const applicationMethods: readonly ApplicationMethod[] = reportAPI.sharedData!.applicationMethods! || [];

  const data: MedicationInfoRow[] = convertToMedicationLabels(records, medicationsList);
  const sortedMedicationRows = sortByHHmm(
    data.flatMap((node) => {
      const timestamp = dayjs(recordIdToTimestamp.get(node.recordId)).format('HH:mm');

      return {
        timestamp,
        medication: node,
      };
    }),
    'timestamp'
  );

  function deleteMedication(recordId: RecordId) {
    const node = nodes.find((node) => node.records.some((record) => record.id === recordId));
    if (node != null) {
      if (node.records.length > 1) {
        throw new Error('Node for deletion contains more than the medication record');
      }
      reportAPI.deleteNode(node.id);
    }
  }

  function addMedication(response: AddMedicationDialogResponse) {
    reportAPI.createNodeWithRecord(
      { type: 'medication', ...response.medication },
      nodeType,
      response.timestamp.valueOf()
    );
  }

  function openAddMedicationDialog() {
    setAddMedicationDialogOpen(true);
  }

  function openAddTimerDialog(medication: MedicationInfoRow) {
    setSuggestedTimerName(
      (medication.source === MedicationSource.CONFIGURATION
        ? medication.activeSubstance
        : medication.customActiveSubstance) ?? ''
    );
    setTimerCorrelatedRecordId(typeof medication.recordId === 'number' ? medication.recordId : undefined);
    setAddTimerDialogOpen(true);
  }

  return (
    <DiviFieldCard cardType={CardId.MedicationTable} recordType={defaultRecords.medication.type} nodeType={nodeType}>
      <div id='medication-area'>
        <AddMedicationDialog
          medicationList={medicationsList}
          applicationMethods={applicationMethods}
          open={addMedicationDialogOpen}
          onOpenChanged={setAddMedicationDialogOpen}
          onConfirm={addMedication}
        />
        <AddTimerDialog
          open={addTimerDialogOpen}
          onOpenChanged={setAddTimerDialogOpen}
          suggestedName={suggestedTimerName}
          correlatedRecordId={timerCorrelatedRecordId}
        />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align='left'>Zeitpunkt</TableCell>
              <TableCell align='left'>Wirkstoff</TableCell>
              <TableCell align='left'>Dosis</TableCell>
              <TableCell align='right'>
                <Button variant='outlined' endIcon={<AddCircleIcon />} onClick={openAddMedicationDialog}>
                  Medikation hinzufügen
                </Button>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedMedicationRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align='center'>
                  Keine Medikation
                </TableCell>
              </TableRow>
            ) : (
              sortedMedicationRows.map(({ medication }) => (
                <TableRow
                  key={
                    (typeof medication.recordId === 'object'
                      ? `optimistic${medication.recordId.optimistic}`
                      : medication.recordId) ?? -1
                  }>
                  <TableCell align='left'>
                    {dayjs(recordIdToTimestamp.get(medication.recordId)).format('HH:mm')}
                  </TableCell>
                  <TableCell align='left'>
                    {medication.source === MedicationSource.CONFIGURATION
                      ? medication.activeSubstance
                      : medication.customActiveSubstance}
                  </TableCell>
                  <TableCell align='left'>
                    {medication.dosage}&nbsp;
                    {medication.unit}
                  </TableCell>
                  <TableCell align='right'>
                    {typeof medication.recordId === 'number' && timerIdentifiers.includes(medication.recordId) ? (
                      <span className='add-timer-button'>Timer angelegt</span>
                    ) : (
                      <Tooltip title='Timer mit Benachrichtigung setzen' arrow>
                        <Chip
                          className='add-timer-button'
                          variant='outlined'
                          avatar={<AlarmAdd />}
                          label='Timer'
                          onClick={() => openAddTimerDialog(medication)}></Chip>
                      </Tooltip>
                    )}
                    <IconButton aria-label='delete' onClick={() => deleteMedication(medication.recordId)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </DiviFieldCard>
  );
}
